import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { DialogActions as DialogActionsInterface } from '@/components/molecules/dialog';
import { Pagination } from '@/components/organisms/pagination';
import { DestinationGroup } from '@/features/destination-groups/api/types/destination-group';
import { useDestinationGroups } from '@/features/destination-groups/hooks/query/use-destination-groups';
import { usePagination } from '@/hooks/use-pagination';

interface Props extends DialogActionsInterface {
  selectedSearchGroup: DestinationGroup | null;
  handleFilterByDestinationGroup: (selectedGroup: DestinationGroup) => void;
}

export const FilterHostsByDestinationGroupDialog: FC<Props> = ({
  onCancel,
  open,
  selectedSearchGroup,
  handleFilterByDestinationGroup,
}) => {
  const { t } = useTranslation(['common', 'host']);

  const [selectedGroup, setSelectedGroup] = useState<DestinationGroup | null>(null);

  const { changePage, changePerPage, page, perPage, setTotalItems, totalPages } = usePagination();

  const { data, isLoading, error, isFetching } = useDestinationGroups({ page, perPage });

  useEffect(() => {
    setSelectedGroup(selectedGroup);
  }, [selectedSearchGroup]);

  const onToggleSelectedDestinationGroupsId = (group: DestinationGroup) => {
    if (group?.id === selectedGroup?.id) {
      setSelectedGroup(null);
    } else {
      setSelectedGroup(group);
    }
  };

  useEffect(() => {
    if (data) {
      setTotalItems(data.total);
    }
  }, [data]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      PaperProps={{
        sx: {
          width: '100%',
        },
      }}>
      <DialogTitle
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}>
        <Typography color="inherit" sx={{ ml: 2 }} variant="inherit">
          {t('host:filterByDestinationGroup.title')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ my: 1 }}>
          {data?.resources?.map(group => {
            return (
              <Card
                onClick={() => onToggleSelectedDestinationGroupsId(group)}
                key={group.id}
                sx={{
                  boxShadow: theme => {
                    return selectedGroup?.id === group.id
                      ? `0px 0px 0px 2px ${theme.palette.primary.main}`
                      : `0px 0px 0px 1px ${theme.palette.divider}`;
                  },
                  cursor: 'pointer',
                  mb: 2,
                }}>
                <CardContent
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    display: 'flex',
                  }}>
                  <Typography color="textPrimary" sx={{ mr: 2 }} variant="h6">
                    {group?.name}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
          <DataStatusIndicator
            isFetching={isLoading}
            error={error?.response?.data?.message}
            dataLength={data?.resources?.length || 0}
          />
        </Box>
        <Pagination
          changePage={changePage}
          page={page}
          changePerPage={changePerPage}
          isFetching={isFetching}
          totalPages={totalPages}
          perPage={perPage}
        />
      </DialogContent>
      <Divider sx={{ mt: 'auto' }} />
      <DialogActions>
        <Button color="primary" onClick={onCancel} variant="text">
          {t('common:button.cancel')}
        </Button>
        <Button
          color="primary"
          disabled={!selectedGroup}
          onClick={() => handleFilterByDestinationGroup(selectedGroup as DestinationGroup)}
          variant="contained">
          {t('common:button.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
